"use client";

import React from "react";
import { Calendar, Tag, User } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

declare const wvcClient: any;

function PostSkeleton() {
  return (
    <div className="animate-pulse w-full">
      <div className="h-8 bg-muted rounded w-3/4 mx-auto mb-4"></div>
      <div className="h-4 bg-muted rounded w-1/2 mx-auto mb-10"></div>
      <div className="h-[400px] bg-muted rounded-2xl w-full mb-12"></div>
      <div className="space-y-4">
        <div className="h-4 bg-muted rounded w-full"></div>
        <div className="h-4 bg-muted rounded w-full"></div>
        <div className="h-4 bg-muted rounded w-5/6"></div>
        <div className="h-4 bg-muted rounded w-full"></div>
        <div className="h-4 bg-muted rounded w-4/6"></div>
      </div>
    </div>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((_, i) => (
          <div key={i} data-index={i} className="animate-pulse">
            <div className="h-48 bg-muted rounded-t-xl"></div>
            <div className="p-4 border border-t-0 border-border rounded-b-xl space-y-3">
              <div className="h-5 bg-muted rounded w-full"></div>
              <div className="h-5 bg-muted rounded w-2/3"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="overflow-hidden hover:shadow-lg transition-shadow h-full flex flex-col border-border bg-card rounded-xl">
          {post.featuredImage && (
            <Link to={post.link || "#"} className="block aspect-video overflow-hidden bg-muted">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </Link>
          )}
          <CardHeader className="p-5 pb-2 flex-1">
            <CardTitle className="text-lg font-default font-bold leading-snug line-clamp-2 hover:text-primary transition-colors text-foreground">
              <Link to={post.link || "#"}>{post.title}</Link>
            </CardTitle>
          </CardHeader>
          <CardFooter className="p-5 pt-4 text-sm text-muted-foreground flex justify-between items-center border-t border-border/30 mt-auto">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-primary" />
              {post.date ? new Date(post.date).toLocaleDateString("ar-EG") : ""}
            </span>
            <Link to={post.link || "#"} className="text-primary font-bold hover:underline text-sm">
              اقرأ المزيد
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection({ currentPostId }: { currentPostId: string }) {
  
  const wp_query = new WP_Query({
    post_type: "post",
    per_page: 3,
    exclude: [currentPostId],
  });

  return (
    <div className="border-t border-border pt-16 mt-16">
      <h3 className="text-2xl font-default font-bold mb-8 text-accent">مقالات تهمك في مكافحة الحشرات</h3>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}

function MainPostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <PostSkeleton />
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        <h2 className="text-2xl font-default font-bold text-accent mb-4">لم يتم العثور على المقال</h2>
        <p className="text-muted-foreground mb-8">عذراً، المقال الذي تبحث عنه غير موجود أو تم حذفه من مدونة سرايا.</p>
        <Link to="/HomePage">
          <Button className="rounded-full px-8 bg-accent text-white hover:bg-accent/90">العودة للرئيسية</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      {}
      <header className="mb-12 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-6 flex flex-wrap justify-center gap-2">
            {post.categories.map((cat, i) => (
              <Link key={cat.id} to={cat.link || "#"}>
                <span
                  data-index={i}
                  className="text-accent text-sm font-bold bg-accent/10 hover:bg-accent/20 transition-colors px-4 py-1.5 rounded-full"
                >
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        )}
        
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-default font-bold mb-6 leading-tight text-accent">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-muted-foreground text-sm md:text-base">
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-accent" />
              <span>{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-accent" />
              <time dateTime={post.date}>{new Date(post.date).toLocaleDateString("ar-EG", { year: 'numeric', month: 'long', day: 'numeric' })}</time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <figure className="mb-16 rounded-xl overflow-hidden shadow-xl bg-muted border border-border/10">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover aspect-video md:aspect-[21/9]"
          />
        </figure>
      )}

      {}
      <div
        className="text-foreground font-default text-lg leading-relaxed mb-16 
                   [&>p]:mb-6 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mb-6 [&>h2]:mt-12 [&>h2]:text-accent [&>h2]:font-default
                   [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:mb-4 [&>h3]:mt-8 [&>h3]:text-accent [&>h3]:font-default
                   [&>ul]:list-disc [&>ul]:ps-6 [&>ul]:mb-6 [&>ul>li]:mb-2
                   [&>ol]:list-decimal [&>ol]:ps-6 [&>ol]:mb-6 [&>ol>li]:mb-2
                   [&>blockquote]:border-s-4 [&>blockquote]:border-accent [&>blockquote]:ps-6 [&>blockquote]:py-4 [&>blockquote]:italic [&>blockquote]:bg-secondary/30 [&>blockquote]:rounded-r-xl [&>blockquote]:mb-6
                   [&>img]:rounded-xl [&>img]:my-8 [&>img]:w-full [&>img]:h-auto [&>img]:shadow-md
                   [&>a]:text-accent [&>a]:font-bold [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-accent/80"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-16">
          <Tag className="w-5 h-5 text-accent me-2" />
          {post.tags.map((tag, i) => (
            <Link key={tag.id} to={tag.link || "#"}>
              <span
                data-index={i}
                className="bg-secondary hover:bg-secondary/80 text-foreground transition-colors px-4 py-2 rounded-xl text-sm font-bold border border-border/10"
              >
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      {}
      {post.author && (
        <Card className="mb-16 bg-secondary/30 border-border/10 shadow-md rounded-xl">
          <CardContent className="p-6 md:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-start">
            <Avatar className="w-24 h-24 border-4 border-background shadow-md shrink-0">
              <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
              <AvatarFallback className="text-2xl bg-accent text-white font-bold">
                {post.author.name.substring(0, 2)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-3">
              <h3 className="text-xl font-default font-bold text-accent">{post.author.name}</h3>
              <p className="text-muted-foreground leading-relaxed font-default">
                خبير متخصص في مجال مكافحة الآفات ورش المبيدات لدى شركة سرايا بالرياض. نلتزم بتقديم أفضل الحلول الآمنة والفعالة لحماية منزلك ومنشأتك من الحشرات والقوارض.
              </p>
              {post.author.link && (
                <div className="pt-2">
                  <Link to={post.author.link}>
                    <Button variant="outline" size="sm" className="rounded-full border-accent text-accent hover:bg-accent hover:text-white font-bold">
                      عرض كل مقالات الخبير
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {}
      <RelatedPostsSection currentPostId={post.id} />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="235488" id="single-post-template-section" className="bg-background min-h-screen text-foreground">
      <PostsProvider wp_query={wp_query}>
        <MainPostConsumer />
      </PostsProvider>
    </section>
  );
}